import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal, EASE } from "@/lib/motion";
import { useBooking } from "@/lib/booking";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";

// Compact page hero used on interior pages.
export const PageHero = ({ eyebrow, lines, italicLast, sub, crumbs, children, accent = false }) => (
  <section className={`${crumbs ? "pt-6" : "pt-40"} pb-16 md:pb-20 bg-white ${accent ? "relative overflow-hidden" : ""}`}>
    {accent && (
      <img
        src="/dandelion-mark.png"
        alt=""
        aria-hidden
        draggable={false}
        className="pointer-events-none select-none absolute -top-12 left-1/2 -translate-x-1/2 w-[300px] md:w-[420px] opacity-[0.16]"
      />
    )}
    {crumbs && <Breadcrumbs items={crumbs} />}
    <div className={`max-w-[1400px] mx-auto px-6 md:px-10 ${accent ? "relative" : ""}`}>
      <p className="text-xs uppercase tracking-[0.25em] text-[#999999] mb-8 mt-8 font-mono">{eyebrow}</p>
      <h1 className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-tight text-[#111111]">
        <MaskedLines lines={lines} delay={0.1} />
        <span className="reveal-line">
          <motion.span
            className="block italic"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 + lines.length * 0.12 }}
          >
            {italicLast}
          </motion.span>
        </span>
      </h1>
      {sub && (
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
          className="mt-8 max-w-3xl text-lg md:text-xl text-[#666666] leading-relaxed"
        >
          {sub}
        </motion.p>
      )}
      {children}
    </div>
  </section>
);

export const PrimaryCTAs = ({ className = "" }) => {
  const { openBooking } = useBooking();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
      className={`mt-10 flex flex-col sm:flex-row flex-wrap gap-4 ${className}`}
    >
      <button type="button" onClick={openBooking} data-testid="page-cta-audit" className="inline-flex items-center gap-2 bg-[#111111] text-white font-semibold px-8 py-4 hover:bg-[#333333] transition-colors group">
        Schedule a Content Audit
        <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
      </button>
      <Link to="/contact" data-testid="page-cta-talk" className="inline-flex items-center gap-2 border border-[#111111] text-[#111111] font-semibold px-8 py-4 hover:bg-[#F5F5F5] transition-colors">
        Talk to Us
      </Link>
    </motion.div>
  );
};

export const SectionHeader = ({ eyebrow, title, sub }) => (
  <Reveal>
    {eyebrow && <p className="text-xs uppercase tracking-[0.25em] text-[#999999] mb-6 font-mono">{eyebrow}</p>}
    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl text-[#111111]">{title}</h2>
    {sub && <p className="mt-6 max-w-2xl text-lg text-[#666666] leading-relaxed">{sub}</p>}
  </Reveal>
);

export const FaqBlock = ({ items }) => (
  <div className="border-t border-[#E0E0E0]">
    {items.map((f, i) => (
      <details key={i} className="group border-b border-[#E0E0E0] py-6 md:py-7">
        <summary className="flex items-start gap-4 cursor-pointer list-none">
          <span className="font-mono text-xs text-[#999999] mt-2 shrink-0">0{i + 1}</span>
          <span className="flex-1 font-serif text-xl md:text-2xl text-[#111111]">{f.q}</span>
          <span className="mt-2 font-mono text-lg text-[#111111] transition-transform group-open:rotate-45">+</span>
        </summary>
        <p className="mt-4 pl-8 pr-8 text-base md:text-lg text-[#666666] leading-relaxed">{f.a}</p>
      </details>
    ))}
  </div>
);
